import React, { useState, useEffect } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import DeleteConfirmationModal from "./menu-delete-modal";
import MenuEditModal from "./menu-edit-modal";
import AddMenuModal from "./menu-add-modal";
import Header from "../../../../components/common/header";
import { useNavigate } from "react-router-dom";
import { GET_SELLER_STORE } from "../../api/seller-store";
import ResponsiveCDNImage from "../../../../components/common/image/responsive-cdn-image";
import { notify } from "../../../../utils/toast";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";

// GraphQL Queries & Mutations
const GET_MENUS = gql`
  query GetMenus($storeId: Int!) {
    menus(storeId: $storeId) {
      id
      name
      price
      img
      description
    }
  }
`;
const CREATE_MENU = gql`
  mutation CreateMenu(
    $storeId: Int!
    $name: String!
    $price: Float!
    $img: Upload
    $description: String
  ) {
    createMenu(
      storeId: $storeId
      name: $name
      price: $price
      img: $img
      description: $description
    ) {
      id
      name
      price
      img
      description
    }
  }
`;
const UPDATE_MENU = gql`
  mutation UpdateMenu(
    $id: Int!
    $name: String
    $price: Float
    $img: Upload
    $description: String
  ) {
    updateMenu(
      id: $id
      name: $name
      price: $price
      img: $img
      description: $description
    ) {
      id
      name
      price
      img
      description
    }
  }
`;

const DELETE_MENU = gql`
  mutation DeleteMenu($id: Int!) {
    deleteMenu(id: $id) {
      id
    }
  }
`;

type MenuItem = {
  id: number;
  name: string;
  price: number;
  img: string;
  description?: string | null;
};
type MenuInput = {
  id: number;
  name: string;
  price: number;
  img: File | null;
  description: string;
};

type Store = {
  id: number;
  img?: string;
  title: string;
  address: string;
  contactNumber: string;
  closingHours: string;
  lat: number;
  lng: number;
};

const MenuManagement: React.FC = () => {
  const storeId = 1; // 상관 없음.
  const navigate = useNavigate();

  const [store, setStore] = useState<Store | null>(null);
  useQuery(GET_SELLER_STORE, {
    onCompleted: (data) => {
      setStore(data.getSellerStore);
    },
  });

  const { data, loading, error, refetch } = useQuery(GET_MENUS, {
    variables: { storeId },
    fetchPolicy: "network-only",
  });

  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState<MenuItem | null>(null);
  const [isDeleting, setIsDeleting] = useState<MenuItem | null>(null); // 삭제할 메뉴 상태 관리

  // GraphQL Mutations
  const [createMenu] = useMutation(CREATE_MENU);
  const [updateMenu] = useMutation(UPDATE_MENU);
  const [deleteMenu] = useMutation(DELETE_MENU);

  useEffect(() => {
    if (data && data.menus) {
      setMenus(data.menus);
    }
  }, [data]);

  const handleAddMenu = () => {
    setIsAdding(true);
  };

  const handleSaveMenu = async (newMenu: MenuInput) => {
    await createMenu({
      variables: {
        storeId,
        name: newMenu.name,
        price: newMenu.price,
        img: newMenu.img,
        description: newMenu.description,
      },
    }).then(() => {
      refetch(); // 메뉴 목록 새로고침
      setIsAdding(false);
      notify("메뉴가 추가되었습니다.");
    });
  };

  const handleEditMenu = (menu: MenuItem) => {
    setIsEditing(menu);
  };

  const handleSaveEditedMenu = (updatedMenu: MenuInput) => {
    updateMenu({
      variables: {
        id: updatedMenu.id,
        name: updatedMenu.name,
        price: updatedMenu.price,
        img: updatedMenu.img,
        description: updatedMenu.description,
      },
    }).then(() => {
      refetch(); // 메뉴 목록 새로고침
      setIsEditing(null);
      notify("메뉴가 수정되었습니다.");
    });
  };

  const handleDeleteMenu = async (id: number) => {
    await deleteMenu({
      variables: { id },
    }).then(() => {
      refetch(); // 메뉴 목록 새로고침
      setIsDeleting(null); // 삭제 후 모달 닫기
      notify("메뉴가 삭제되었습니다.");
    });
  };

  if (loading) return <LoadingSpinnerFull />;
  if (error) return <p>Error: {error.message}</p>;

  if (!store)
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <p className="text-lg mb-6 text-gray-700">
          매장이 아직 등록되지 않았습니다.
        </p>
        <button
          onClick={() => navigate("/boss/my/store-management")}
          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          매장 추가하러 가기
        </button>
      </div>
    );

  return (
    <>
      <Header title="메뉴관리" showBackButton />
      <div className="p-6">
        {/* 메뉴 추가 버튼 */}
        <p className="text-3xl p-4 text-center bg-gray-100 rounded-lg mb-4">
          메뉴를 미리 추가하여 <br />
          실시간 판매를 준비해보세요!
        </p>
        {/* 메뉴 목록이 없을 경우 */}
        {menus.length === 0 ? (
          <div>
            <div
              className="w-1/2 flex flex-col justify-center items-center h-full bg-white border-2 border-dashed border-gray-300 rounded-lg py-4"
              onClick={handleAddMenu}
            >
              <div className="text-4xl pb-1 font-bold text-white rounded-full bg-green-500 h-10 w-10 flex items-center justify-center">
                +
              </div>
              <div className="text-3xl text-gray-500">메뉴 추가</div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {menus.map((menu) => (
              <div key={menu.id} className="border rounded-lg">
                <ResponsiveCDNImage
                  src={menu.img}
                  alt={menu.name}
                  className="h-40 w-full object-cover rounded-md mb-2"
                  sizeRatio={1 / 2}
                />
                <div className="p-4">
                  <h2 className="text-lg font-semibold">{menu.name}</h2>
                  <p className="text-sm text-gray-600">
                    {menu.price.toLocaleString()}원
                  </p>
                  {menu.description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {menu.description}
                    </p>
                  )}
                  <div className="flex justify-between mt-2">
                    <button
                      className="px-3 py-1 bg-green-500 text-white text-sm rounded"
                      onClick={() => handleEditMenu(menu)}
                    >
                      수정
                    </button>
                    <button
                      className="px-3 py-1 bg-red-500 text-white text-sm rounded"
                      onClick={() => setIsDeleting(menu)}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              </div>
            ))}
            <div
              className="col-span-1 flex flex-col justify-center items-center w-full h-full bg-white border-2 border-dashed border-gray-300 rounded-lg"
              onClick={handleAddMenu}
            >
              <div className="text-4xl pb-1 font-bold text-white rounded-full bg-green-500 h-10 w-10 flex items-center justify-center">
                +
              </div>
              <div className="text-3xl text-gray-500">메뉴 추가</div>
            </div>
          </div>
        )}

        {/* 메뉴 추가 모달 */}
        {isAdding && (
          <AddMenuModal
            onClose={() => setIsAdding(false)}
            onSave={handleSaveMenu}
          />
        )}

        {/* 메뉴 수정 모달 */}
        {isEditing && (
          <MenuEditModal
            menu={isEditing as any}
            onClose={() => setIsEditing(null)}
            onSave={handleSaveEditedMenu}
          />
        )}

        {/* 삭제 확인 모달 */}
        {isDeleting && (
          <DeleteConfirmationModal
            onClose={() => setIsDeleting(null)}
            onConfirm={() => handleDeleteMenu(isDeleting.id)}
          />
        )}
      </div>
    </>
  );
};

export default MenuManagement;
